import { useEffect, useRef, type ReactNode } from 'react';
import { Icon } from '@hive/ui';

interface Props {
  titleId: string;
  title: ReactNode;
  subtitle?: ReactNode;
  onClose(): void;
  children: ReactNode;
  footer?: ReactNode;
  width?: number;
}

/** Centred modal over a deep-blue scrim, as in the picker design. */
export function Dialog({ titleId, title, subtitle, onClose, children, footer, width = 680 }: Props) {
  const ref = useRef<HTMLDivElement>(null);
  // Parents pass a new onClose on every render (and pages re-render on every clock tick or
  // live update): keep the latest in a ref, so the effect below runs once. Re-running it
  // would pull focus back to the dialog while someone is typing in one of its fields.
  const closeRef = useRef(onClose);
  // Close on a click that starts and ends on the scrim. A drag that starts in a field
  // (selecting its text) and is released outside the dialog fires its click on the scrim
  // too, and must not throw away what was typed.
  const downOnScrim = useRef(false);
  closeRef.current = onClose;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeRef.current();
    document.addEventListener('keydown', onKey);
    ref.current?.focus();
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  return (
    <div
      className="k-scrim"
      onMouseDown={(e) => (downOnScrim.current = e.target === e.currentTarget)}
      onClick={(e) => {
        if (downOnScrim.current && e.target === e.currentTarget) onClose();
        downOnScrim.current = false;
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className="k-dialog"
        style={{ width }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="k-dialog__head">
          <div className="k-dialog__titles">
            <h1 id={titleId} className="k-dialog__title">
              {title}
            </h1>
            {subtitle && <span className="k-dialog__subtitle">{subtitle}</span>}
          </div>
          <button type="button" className="k-close" aria-label="Close" onClick={onClose}>
            <Icon name="close" size={18} />
          </button>
        </div>
        <div className="k-dialog__body">{children}</div>
        {footer && <div className="k-dialog__foot">{footer}</div>}
      </div>
    </div>
  );
}
